import { useParams } from 'react-router-dom'
import { ProjectLayout } from '@/components/layout/ProjectLayout'
import { useProject } from '@/hooks/useProjects'
import { useTranscodes, useUploadEDL, useMarkTranscodeComplete, useDeleteTranscode } from '@/hooks/useTranscodes'
import { usePermissions } from '@/hooks/usePermissions'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { EntityDetailSheet } from '@/components/shared/EntityDetailSheet'
import { TranscodeDetailContent } from '@/components/transcodes/TranscodeDetailContent'
import { Upload, Package, CheckCircle, Clock, FileText, AlertCircle, Trash2 } from 'lucide-react'
import { useState } from 'react'
import type { TranscodeStatus } from '@/types/database'
import { useAnalytics } from '@/hooks/useAnalytics'

export function ProjectTranscodesPage() {
  const { id } = useParams<{ id: string }>()
  const { data: project, isLoading: projectLoading } = useProject(id)
  const { data: transcodes, isLoading: transcodesLoading } = useTranscodes(id || '')
  const permissions = usePermissions(id, project?.owner_user_id)
  const uploadEDL = useUploadEDL()
  const markComplete = useMarkTranscodeComplete()
  const deleteTranscode = useDeleteTranscode()
  const { track } = useAnalytics()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [vendor, setVendor] = useState('')
  const [notes, setNotes] = useState('')
  const [detailSheetOpen, setDetailSheetOpen] = useState(false)
  const [detailTranscodeId, setDetailTranscodeId] = useState<string | null>(null)

  const isLoading = projectLoading || transcodesLoading

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleUpload = () => {
    if (!id || !selectedFile) return

    uploadEDL.mutate(
      {
        projectId: id,
        file: selectedFile,
        vendor: vendor || undefined,
        notes: notes || undefined,
        fps: 24,
      },
      {
        onSuccess: () => {
          // Track transcode upload
          track('transcode_uploaded', {
            project_id: id,
            vendor: vendor || undefined,
            has_notes: !!notes,
          })

          // Reset form
          setSelectedFile(null)
          setVendor('')
          setNotes('')
          // Reset file input
          const fileInput = document.getElementById('edl-file-input') as HTMLInputElement
          if (fileInput) fileInput.value = ''
        },
      }
    )
  }

  const handleMarkComplete = (transcodeId: string) => {
    markComplete.mutate(
      {
        transcodeId,
        notes: 'Marked complete from UI',
      },
      {
        onSuccess: () => {
          // Track transcode completed
          track('transcode_completed', {
            project_id: id!,
            transcode_id: transcodeId,
          })
        },
      }
    )
  }

  const handleDelete = (transcodeId: string, transcodeIdName: string) => {
    if (window.confirm(`Are you sure you want to delete transcode ${transcodeIdName}? This will unlink VFX shots but not delete them.`)) {
      deleteTranscode.mutate(
        { transcodeId, projectId: id || '' },
        {
          onSuccess: () => {
            // Track transcode deletion
            track('transcode_deleted', {
              project_id: id!,
              transcode_id: transcodeId,
            })
          },
        }
      )
    }
  }

  const handleOpenDetails = (transcodeId: string) => {
    // Track transcode viewed
    track('transcode_viewed', {
      project_id: id!,
      transcode_id: transcodeId,
    })

    setDetailTranscodeId(transcodeId)
    setDetailSheetOpen(true)
  }

  const getStatusIcon = (status: TranscodeStatus) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusBadgeVariant = (status: TranscodeStatus) => {
    switch (status) {
      case 'complete':
        return 'default'
      case 'pending':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  if (isLoading) {
    return (
      <ProjectLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading transcodes...</p>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  return (
    <ProjectLayout projectName={project?.name}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Transcodes</h2>
            <p className="text-muted-foreground">
              EDL-based vendor pulls and transcode requests
            </p>
          </div>
        </div>

        {/* Upload EDL Section */}
        {permissions.canCreateTranscodes && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload EDL
              </CardTitle>
              <CardDescription>
                Upload a CMX 3600 EDL file to create transcode requests and link VFX shots
              </CardDescription>
            </CardHeader>
            <CardContent>
            <div className="space-y-4">
              {/* File Input */}
              <div className="space-y-2">
                <Label htmlFor="edl-file-input">EDL File</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="edl-file-input"
                    type="file"
                    accept=".edl"
                    onChange={handleFileChange}
                    className="flex-1"
                  />
                  {selectedFile && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      <span>{selectedFile.name}</span>
                      <span className="text-xs">({(selectedFile.size / 1024).toFixed(2)} KB)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Vendor Input */}
              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor (Optional)</Label>
                <Input
                  id="vendor"
                  placeholder="e.g., Framestore, ILM, DNEG"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                />
              </div>

              {/* Notes Input */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about this transcode request"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Upload Button */}
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploadEDL.isPending}
                className="w-full"
              >
                {uploadEDL.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload EDL
                  </>
                )}
              </Button>

              {/* Info */}
              <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted p-3 rounded-md">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">EDL Format Requirements:</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>CMX 3600 format</li>
                    <li>Include TITLE line (e.g., "TITLE: TC001")</li>
                    <li>Include vendor filenames in comment lines (e.g., "* FROM CLIP NAME: SHOT_010_0010_FG1_v001")</li>
                    <li>Clips must exist in project with matching tape_name and source_timecode</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Transcode Requests List */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">Transcode Requests</h3>

          {transcodes && transcodes.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {transcodes.map((transcode) => (
                <Card
                  key={transcode.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleOpenDetails(transcode.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-primary" />
                        <div>
                          <CardTitle className="text-lg">{transcode.transcode_id}</CardTitle>
                          <CardDescription>
                            {transcode.vendor || 'No vendor specified'}
                          </CardDescription>
                        </div>
                      </div>
                      <Badge variant={getStatusBadgeVariant(transcode.status)}>
                        <span className="flex items-center gap-1">
                          {getStatusIcon(transcode.status)}
                          {transcode.status}
                        </span>
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Created</span>
                        <span className="font-medium">
                          {new Date(transcode.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {transcode.marked_complete_date && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Completed</span>
                          <span className="font-medium">
                            {new Date(transcode.marked_complete_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {transcode.notes && (
                        <div className="pt-2 border-t">
                          <p className="text-muted-foreground line-clamp-2">{transcode.notes}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {permissions.canCreateTranscodes && (
                        <div className="pt-3 border-t">
                          <div className="flex gap-2">
                            {transcode.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleMarkComplete(transcode.id)
                                }}
                                disabled={markComplete.isPending}
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                Mark Complete
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className={transcode.status === 'pending' ? '' : 'w-full'}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDelete(transcode.id, transcode.transcode_id)
                              }}
                              disabled={deleteTranscode.isPending}
                            >
                              <Trash2 className="mr-2 h-4 w-4 text-destructive" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Transcode Requests</h3>
                  <p className="text-muted-foreground mb-6">
                    Upload an EDL file to create your first transcode request
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Transcode Detail Sidebar */}
        {detailTranscodeId && (
          <EntityDetailSheet
            open={detailSheetOpen}
            onOpenChange={setDetailSheetOpen}
            title={
              transcodes?.find((t) => t.id === detailTranscodeId)?.transcode_id ||
              'Transcode Details'
            }
            subtitle="Transcode Request"
            width="xl"
          >
            <TranscodeDetailContent transcodeId={detailTranscodeId} />
          </EntityDetailSheet>
        )}
      </div>
    </ProjectLayout>
  )
}
